import { useState, useEffect, useCallback, useRef } from 'react'
import { DragStartEvent, DragEndEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { uploadFileToUrl, compressImage, validateImageFile } from '@wtf/shared'
import { COMPRESSION_PRESETS } from '@/config/compressionPresets'

export interface ImageItem {
    id: string
    url: string
}

export interface UploadError {
    fileName: string
    error: string
    timestamp: number
}

export type PresignParams = {
    fileName: string
    contentType: string
}

export type PresignResult = {
    uploadUrl: string
    publicUrl: string
    key: string
}

export interface UseImageGridOptions {
    initialUrls: string[]
    getPresign: (params: PresignParams) => Promise<PresignResult>
    onChange: (urls: string[]) => void
    maxImages?: number
    minImages?: number
    // Add option to skip upload for already-uploaded images
    skipUploadForExisting?: boolean
}

export function useImageGrid({
    initialUrls,
    getPresign,
    onChange,
    maxImages = 10,
    minImages,
    skipUploadForExisting = false,
}: UseImageGridOptions) {
    const [imageItems, setImageItems] = useState<ImageItem[]>([])
    const [uploadProgress, setUploadProgress] = useState<
        Record<string, number>
    >({})
    const [uploadErrors, setUploadErrors] = useState<UploadError[]>([])
    const [isUploading, setIsUploading] = useState(false)
    const [activeImageId, setActiveImageId] = useState<string | null>(null)
    const [isDragActive, setIsDragActive] = useState(false)
    const [isInitialized, setIsInitialized] = useState(false)
    const hasUploadedInitialRef = useRef(false)
    const initialUrlsRef = useRef<string[]>([])

    // Helper function to upload external image using hybrid approach
    const uploadExternalImage = useCallback(
        async (imageUrl: string, fileName: string): Promise<string> => {
            console.log(
                `[useImageGrid] 🔄 Starting client-side upload for: ${imageUrl}`
            )

            try {
                console.log(
                    `[useImageGrid] 📥 Fetching image for upload: ${imageUrl}`
                )
                const response = await fetch(imageUrl)
                if (!response.ok)
                    throw new Error(
                        `HTTP ${response.status}: ${response.statusText}`
                    )
                const blob = await response.blob()
                if (!blob.type.startsWith('image/'))
                    throw new Error(`Invalid content type: ${blob.type}`)
                const file = new File([blob], fileName, { type: blob.type })

                // Compress the image
                const compressedFile = await compressImage(
                    file,
                    COMPRESSION_PRESETS.PRODUCT
                )
                const presignResponse = await getPresign({
                    fileName: compressedFile.name,
                    contentType: compressedFile.type,
                })
                await uploadFileToUrl(presignResponse.uploadUrl, compressedFile)
                console.log(
                    `[useImageGrid] ✅ Client-side upload complete: ${presignResponse.publicUrl}`
                )
                return presignResponse.publicUrl
            } catch (clientError) {
                const message =
                    clientError instanceof Error
                        ? clientError.message
                        : String(clientError)
                console.warn(
                    `[useImageGrid] 🚨 Client-side fetch failed: ${message}`
                )
                console.log(
                    `[useImageGrid] 🔄 Backend fallback is temporarily disabled for: ${imageUrl}`
                )
                throw clientError
            }
        },
        [getPresign]
    )

    // Helper function to determine if a URL is already uploaded to our storage
    const isAlreadyUploaded = useCallback(
        (url: string) => {
            console.log(
                `[useImageGrid] 🔍 Checking if URL is already uploaded: ${url}`
            )

            // URLs from our R2 bucket or CDN don't need re-uploading
            const ourDomains = [
                'wtf.style',
                'r2.wtf.style',
                'cdn.wtf.style',
                'whatthefuckareyouwearing.com', // Legacy domain support
                'r2.whatthefuckareyouwearing.com', // Legacy domain support
                'cdn.whatthefuckareyouwearing.com', // Legacy domain support
            ]

            // Check if URL contains our domain patterns
            const isOurDomain = ourDomains.some((domain) =>
                url.includes(domain)
            )
            console.log(`[useImageGrid] isOurDomain: ${isOurDomain}`)

            // Check for R2 bucket patterns
            const isR2Bucket =
                url.startsWith('https://r2.') || url.includes('/r2/')
            console.log(`[useImageGrid] isR2Bucket: ${isR2Bucket}`)

            // If skipUploadForExisting is true, assume any URL that doesn't look like a common external domain is already uploaded
            const isLikelyExternal =
                skipUploadForExisting &&
                (url.includes('acnestudios.com') ||
                    url.includes('shopify.com') ||
                    url.includes('amazonaws.com') ||
                    url.includes('cloudfront.net') ||
                    (url.includes('cdn.') &&
                        !url.includes('wtf.style') &&
                        !url.includes('whatthefuckareyouwearing.com'))) // Legacy domain support

            console.log(
                `[useImageGrid] skipUploadForExisting: ${skipUploadForExisting}`
            )
            console.log(`[useImageGrid] isLikelyExternal: ${isLikelyExternal}`)

            const result =
                isOurDomain ||
                isR2Bucket ||
                (skipUploadForExisting && !isLikelyExternal)
            console.log(`[useImageGrid] isAlreadyUploaded result: ${result}`)

            return result
        },
        [skipUploadForExisting]
    )

    // Initialize items from URLs once
    useEffect(() => {
        if (initialUrls.length === 0) {
            setIsInitialized(true)
            return
        }

        // Only initialize if URLs have actually changed
        const urlsChanged =
            JSON.stringify(initialUrls) !==
            JSON.stringify(initialUrlsRef.current)
        if (!urlsChanged && hasUploadedInitialRef.current) return

        initialUrlsRef.current = initialUrls
        hasUploadedInitialRef.current = false

        const initialItems = initialUrls.map((url, index) => ({
            id: `initial-${index}-${Date.now()}`,
            url,
        }))

        setImageItems(initialItems)
        setIsInitialized(true)
    }, [initialUrls])

    // Upload initial URLs after items are set
    useEffect(() => {
        console.log('[useImageGrid] 🚀 Upload trigger useEffect - START')
        console.log('[useImageGrid] isInitialized:', isInitialized)
        console.log(
            '[useImageGrid] hasUploadedInitialRef.current:',
            hasUploadedInitialRef.current
        )
        console.log('[useImageGrid] imageItems.length:', imageItems.length)
        console.log('[useImageGrid] imageItems:', imageItems)

        if (!isInitialized) {
            console.log(
                '[useImageGrid] ❌ Upload not starting - not initialized'
            )
            return
        }

        if (hasUploadedInitialRef.current) {
            console.log(
                '[useImageGrid] ❌ Upload not starting - already uploaded'
            )
            return
        }

        if (imageItems.length === 0) {
            console.log('[useImageGrid] ❌ Upload not starting - no items')
            return
        }

        // Only upload if we have initial items (not user-uploaded items)
        const hasInitialItems = imageItems.some((item) =>
            item.id.startsWith('initial-')
        )
        console.log('[useImageGrid] hasInitialItems:', hasInitialItems)
        console.log(
            '[useImageGrid] Initial items:',
            imageItems.filter((item) => item.id.startsWith('initial-'))
        )

        if (!hasInitialItems) {
            console.log(
                '[useImageGrid] ❌ Upload not starting - no initial items'
            )
            return
        }

        console.log(
            '[useImageGrid] ✅ All conditions met - starting upload process'
        )

        async function uploadInitial() {
            console.log('[useImageGrid] 🔄 uploadInitial function called')
            hasUploadedInitialRef.current = true

            const initialItems = imageItems.filter((item) =>
                item.id.startsWith('initial-')
            )
            console.log(
                '[useImageGrid] Initial items to process:',
                initialItems
            )

            // Filter out items that are already uploaded to avoid unnecessary uploads
            const itemsToUpload = initialItems.filter(
                (item) => !isAlreadyUploaded(item.url)
            )
            const itemsAlreadyUploaded = initialItems.filter((item) =>
                isAlreadyUploaded(item.url)
            )

            console.log('[useImageGrid] Initial URLs analysis:', {
                total: initialItems.length,
                toUpload: itemsToUpload.length,
                alreadyUploaded: itemsAlreadyUploaded.length,
                skipUploadForExisting,
            })

            console.log('[useImageGrid] Items to upload:', itemsToUpload)
            console.log(
                '[useImageGrid] Items already uploaded:',
                itemsAlreadyUploaded
            )

            // If no items need uploading, we're done
            if (itemsToUpload.length === 0) {
                console.log(
                    '[useImageGrid] All images are already uploaded, skipping upload process'
                )
                return
            }

            console.log(
                '[useImageGrid] Starting initial scraped image uploads for URLs:',
                itemsToUpload.map((i) => i.url)
            )
            setIsUploading(true)
            setUploadErrors([])
            console.log(
                '[useImageGrid] Cleared previous errors, beginning upload of',
                itemsToUpload.length,
                'scraped images'
            )

            const promises = itemsToUpload.map(async (item, idx) => {
                console.log(
                    `[useImageGrid] Initial upload: processing item ${item.id} with url ${item.url}`
                )
                try {
                    setUploadProgress((prev) => ({ ...prev, [item.url]: 0 }))
                    console.log(
                        `[useImageGrid] Initial upload: set progress 0% for ${item.url}`
                    )

                    // Use hybrid approach instead of direct fetch
                    const filename =
                        item.url.split('/').pop() || `image-${idx}.jpg`
                    const hybridFileName = `scraped-${Date.now()}-${idx}-${filename}`

                    setUploadProgress((prev) => ({ ...prev, [item.url]: 30 }))

                    // Use the hybrid upload function
                    const publicUrl = await uploadExternalImage(
                        item.url,
                        hybridFileName
                    )

                    setUploadProgress((prev) => ({ ...prev, [item.url]: 100 }))
                    console.log(
                        `[useImageGrid] Initial upload: upload complete, publicUrl: ${publicUrl}`
                    )

                    console.log(
                        `[useImageGrid] 🔄 UPDATING imageItems: ${item.url} -> ${publicUrl}`
                    )
                    console.log(
                        `[useImageGrid] Item ID being updated: ${item.id}`
                    )

                    setImageItems((prev) => {
                        const updated = prev.map((i) => {
                            if (i.id === item.id) {
                                console.log(
                                    `[useImageGrid] ✅ Updated item ${i.id}: ${i.url} -> ${publicUrl}`
                                )
                                return { ...i, url: publicUrl }
                            }
                            return i
                        })
                        console.log(
                            `[useImageGrid] Updated imageItems:`,
                            updated
                        )
                        // Immediately sync form with new R2 URLs
                        onChange(updated.map((i) => i.url))
                        return updated
                    })

                    setUploadProgress((prev) => {
                        const { [item.url]: _, ...rest } = prev
                        return rest
                    })
                } catch (error) {
                    console.log(
                        `[useImageGrid] Initial upload error for ${item.url}:`,
                        error
                    )
                    // For scraped images, silently remove the failed image instead of showing an error
                    setImageItems((prev) =>
                        prev.filter((i) => i.id !== item.id)
                    )
                    setUploadProgress((prev) => {
                        const { [item.url]: _, ...rest } = prev
                        return rest
                    })
                }
            })

            await Promise.allSettled(promises)
            console.log(
                '[useImageGrid] All initial scraped image uploads settled'
            )
            setIsUploading(false)
        }

        uploadInitial()
    }, [
        isInitialized,
        getPresign,
        isAlreadyUploaded,
        uploadExternalImage,
        onChange,
    ])

    // Call onChange when imageItems change
    useEffect(() => {
        console.log(`[useImageGrid] 📡 onChange useEffect triggered`)
        console.log(`[useImageGrid] isInitialized: ${isInitialized}`)
        console.log(`[useImageGrid] imageItems:`, imageItems)

        if (isInitialized) {
            const urls = imageItems.map((item) => item.url)
            console.log(`[useImageGrid] 🔄 Calling onChange with urls:`, urls)

            // Check if URLs are R2 URLs
            const areR2URLs = urls.every(
                (url) =>
                    url.includes('wtf.style') ||
                    url.includes('whatthefuckareyouwearing.com') || // Legacy domain support
                    url.includes('r2.') ||
                    url.startsWith('https://r2.')
            )

            console.log(`[useImageGrid] Are URLs R2 URLs? ${areR2URLs}`)

            onChange(urls)
            console.log(`[useImageGrid] ✅ onChange called successfully`)
        }
        console.log(`[useImageGrid] 📡 onChange useEffect completed`)
    }, [imageItems, isInitialized, onChange]) // Removed onChange from dependencies to prevent infinite loops

    const uploadImages = useCallback(
        async (files: FileList) => {
            if (files.length === 0) return
            console.log(
                `[useImageGrid] Starting user-selected image upload for ${files.length} file(s)`
            )
            setIsUploading(true)
            setUploadErrors([])
            const existingCount = imageItems.length
            const fileArray = Array.from(files).slice(
                0,
                Math.max(0, maxImages - existingCount)
            )
            const newItems: ImageItem[] = fileArray.map((file, idx) => ({
                id: `upload-${Date.now()}-${idx}`,
                url: URL.createObjectURL(file),
            }))
            setImageItems((prev) => [...prev, ...newItems])
            console.log(
                '[useImageGrid] Created previews for new items:',
                newItems
            )

            const promises = fileArray.map(async (file, idx) => {
                const item = newItems[idx]
                console.log(
                    `[useImageGrid] Uploading file: ${file.name}, temp url: ${item.url}`
                )
                try {
                    setUploadProgress((prev) => ({ ...prev, [item.url]: 0 }))

                    // Validate file
                    const validationError = validateImageFile(file)
                    if (validationError) {
                        throw new Error(validationError)
                    }

                    console.log(
                        `[useImageGrid] Uploading file: ${file.name}, progress 0%`
                    )

                    // Compress the image for products (HEIC conversion handled automatically)
                    const compressedFile = await compressImage(
                        file,
                        COMPRESSION_PRESETS.PRODUCT
                    )

                    const respUser = await getPresign({
                        fileName: compressedFile.name,
                        contentType: compressedFile.type,
                    })
                    console.log(
                        '[useImageGrid] presign response (user):',
                        respUser
                    )
                    const { uploadUrl, publicUrl, key } = respUser
                    const fallbackUrlUser = `https://r2.wtf.style/${key}`
                    console.log(
                        '[useImageGrid] constructed fallbackUrl (user):',
                        fallbackUrlUser
                    )
                    console.log(
                        `[useImageGrid] Received presign for ${compressedFile.name}`,
                        uploadUrl
                    )
                    setUploadProgress((prev) => ({ ...prev, [item.url]: 50 }))
                    console.log(
                        `[useImageGrid] Uploading file ${compressedFile.name} to R2`
                    )
                    await uploadFileToUrl(uploadUrl, compressedFile)
                    console.log(
                        `[useImageGrid] Upload complete for ${compressedFile.name}, publicUrl: ${publicUrl}`
                    )
                    setUploadProgress((prev) => ({ ...prev, [item.url]: 100 }))
                    setImageItems((prev) =>
                        prev.map((i) =>
                            i.id === item.id ? { ...i, url: publicUrl } : i
                        )
                    )
                    URL.revokeObjectURL(item.url)
                    setUploadProgress((prev) => {
                        const { [item.url]: _, ...rest } = prev
                        return rest
                    })
                } catch (error) {
                    console.log(
                        `[useImageGrid] Error uploading user file ${file.name}:`,
                        error
                    )
                    setUploadErrors((prev) => [
                        ...prev,
                        {
                            fileName: file.name,
                            error:
                                error instanceof Error
                                    ? error.message
                                    : String(error),
                            timestamp: Date.now(),
                        },
                    ])
                    setImageItems((prev) =>
                        prev.filter((i) => i.id !== item.id)
                    )
                    URL.revokeObjectURL(item.url)
                    setUploadProgress((prev) => {
                        const { [item.url]: _, ...rest } = prev
                        return rest
                    })
                }
            })

            await Promise.allSettled(promises)
            console.log(
                '[useImageGrid] All user-selected image uploads settled'
            )
            setIsUploading(false)
        },
        [getPresign, imageItems.length, maxImages]
    )

    const removeImage = useCallback(
        (id: string) => {
            setImageItems((prev) => {
                // Check if removing would go below minimum (only if minImages is specified)
                if (minImages !== undefined && prev.length <= minImages) {
                    return prev
                }
                const toRemove = prev.find((item) => item.id === id)
                if (toRemove?.url.startsWith('blob:'))
                    URL.revokeObjectURL(toRemove.url)
                return prev.filter((item) => item.id !== id)
            })
        },
        [minImages]
    )

    const clearErrors = useCallback(() => setUploadErrors([]), [])

    const onDragStart = useCallback((event: DragStartEvent) => {
        setActiveImageId(event.active.id as string)
        setIsDragActive(true)
    }, [])

    const onDragEnd = useCallback((event: DragEndEvent) => {
        setActiveImageId(null)
        setIsDragActive(false)
        const { active, over } = event
        if (!over || active.id === over.id) return
        setImageItems((prev) => {
            const oldIndex = prev.findIndex((i) => i.id === active.id)
            const newIndex = prev.findIndex((i) => i.id === over.id)
            if (oldIndex === -1 || newIndex === -1) return prev
            return arrayMove(prev, oldIndex, newIndex)
        })
    }, [])

    const onDragCancel = useCallback(() => {
        setActiveImageId(null)
        setIsDragActive(false)
    }, [])

    const hasExcessImages = imageItems.length > maxImages

    return {
        imageItems,
        uploadProgress,
        uploadErrors,
        isUploading,
        hasExcessImages,
        uploadImages,
        removeImage,
        clearErrors,
        activeImageId,
        isDragActive,
        onDragStart,
        onDragEnd,
        onDragCancel,
        isInitialized,
    }
}
